var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useMemo, useState } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { useInfiniteQuery } from 'react-query';
import { useOkapiKy } from '@folio/stripes/core';
import { Accordion, Button, Loading, MessageBanner, } from '@folio/stripes/components';
import { getRecordHistory } from '../../api/recordService';
import useUserNames from '../../hooks/useUserNames';
/** Versions fetched per page ("Načíst starší verze" fetches the next one). */
export var VERSIONS_PER_PAGE = 20;
/** Changed subfields shown in an expanded version before "Zobrazit všech N". */
export var CHANGES_SHOWN_COLLAPSED = 20;
var cellStyle = {
    textAlign: 'left',
    padding: '4px 8px',
    verticalAlign: 'top',
};
var headStyle = __assign(__assign({}, cellStyle), { borderBottom: '1px solid #ccc' });
/** `100 $a`, `001` for control fields; indicators when either is set, `#` for blank. */
export var fieldLabel = function (change) {
    var ind = function (v) { return (v == null || v === ' ' ? '#' : v); };
    var hasIndicators = change.indicator1 != null || change.indicator2 != null;
    var indicators = hasIndicators ? " ".concat(ind(change.indicator1)).concat(ind(change.indicator2)) : '';
    var subfield = change.subfieldCode ? " $".concat(change.subfieldCode) : '';
    return "".concat(change.fieldTag).concat(indicators).concat(subfield);
};
var ChangesTable = function (_a) {
    var changes = _a.changes;
    var _b = useState(false), showAll = _b[0], setShowAll = _b[1];
    var visible = showAll ? changes : changes.slice(0, CHANGES_SHOWN_COLLAPSED);
    var dash = function (v) { return (v == null || v === '' ? '–' : v); };
    return (React.createElement("div", null,
        React.createElement("table", { "data-testid": "record-history-changes", style: { width: '100%', borderCollapse: 'collapse', marginTop: '4px' } },
            React.createElement("thead", null,
                React.createElement("tr", null,
                    React.createElement("th", { style: headStyle },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.history.column.field" })),
                    React.createElement("th", { style: headStyle },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.history.column.oldValue" })),
                    React.createElement("th", { style: headStyle },
                        React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.history.column.newValue" })))),
            React.createElement("tbody", null, visible.map(function (c) {
                var _a;
                return (React.createElement("tr", { key: "".concat(c.fieldTag, "-").concat(c.fieldIndex, "-").concat((_a = c.subfieldCode) !== null && _a !== void 0 ? _a : '', "-").concat(c.subfieldIndex, "-").concat(c.operation), "data-testid": "record-history-change-row" },
                    React.createElement("td", { style: __assign(__assign({}, cellStyle), { whiteSpace: 'nowrap', fontFamily: 'monospace' }) },
                        fieldLabel(c),
                        c.operation === 'REORDER' && (React.createElement("div", { style: { color: '#666', fontSize: '0.85em', fontFamily: 'initial' } },
                            React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.history.reorder" })))),
                    React.createElement("td", { style: __assign(__assign({}, cellStyle), { color: c.operation === 'INSERT' ? '#999' : undefined }) }, dash(c.oldValue)),
                    React.createElement("td", { style: __assign(__assign({}, cellStyle), { color: c.operation === 'DELETE' ? '#999' : undefined }) },
                        dash(c.newValue),
                        c.operation === 'UPDATE' && c.oldValue === c.newValue && (React.createElement("div", { style: { color: '#666', fontSize: '0.85em' } },
                            React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.history.sameValue" }))))));
            }))),
        !showAll && changes.length > CHANGES_SHOWN_COLLAPSED && (React.createElement(Button, { buttonStyle: "link", onClick: function () { return setShowAll(true); }, "data-testid": "record-history-show-all", marginBottom0: true },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.history.showAll", values: { count: changes.length } })))));
};
var VersionRow = function (_a) {
    var entry = _a.entry, userName = _a.userName;
    var intl = useIntl();
    var _b = useState(false), expanded = _b[0], setExpanded = _b[1];
    var operation = intl.formatMessage({
        id: "ui-czna-authorities.authorities.detail.history.operation.".concat(entry.operationType),
        defaultMessage: entry.operationType,
    });
    return (React.createElement("li", { "data-testid": "record-history-version", style: { borderBottom: '1px solid #e5e5e5', padding: '8px 0', listStyle: 'none' } },
        React.createElement("div", { style: { display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' } },
            entry.version != null && (React.createElement("strong", null,
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.history.version", values: { version: entry.version } }))),
            React.createElement("span", null,
                React.createElement(FormattedDate, { value: entry.changedAt, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit" })),
            React.createElement("span", null, userName),
            React.createElement("span", { style: { color: '#666' } }, operation),
            entry.contentChanged ? (React.createElement(Button, { buttonStyle: "link", onClick: function () { return setExpanded(function (e) { return !e; }); }, "aria-expanded": expanded, "data-testid": "record-history-toggle", marginBottom0: true },
                React.createElement(FormattedMessage, { id: expanded
                        ? 'ui-czna-authorities.authorities.detail.history.hideChanges'
                        : 'ui-czna-authorities.authorities.detail.history.showChanges', values: { count: entry.changes.length } }))) : entry.changesUnavailable ? (React.createElement("span", { "data-testid": "record-history-changes-unavailable", style: { color: '#666' } },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.history.changesUnavailable" }))) : (React.createElement("span", { "data-testid": "record-history-no-content-change", style: { color: '#666' } },
                React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.history.noContentChange" })))),
        entry.reviewComment && (React.createElement("div", { style: { color: '#666', marginTop: '4px' } }, entry.reviewComment)),
        expanded && entry.contentChanged && React.createElement(ChangesTable, { changes: entry.changes })));
};
/**
 * The heading forms the Aleph import carried over (MOR/COR). Aleph kept neither a date nor
 * an author for them and their order is its export order, so they are listed as forms, not
 * dated versions — and always last, because the import precedes any work in this system.
 */
var ImportedHeadingsRow = function (_a) {
    var entry = _a.entry;
    return (React.createElement("li", { "data-testid": "record-history-imported-headings", style: { padding: '8px 0', listStyle: 'none' } },
        React.createElement("strong", null,
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.history.imported.label" })),
        React.createElement("div", { style: { color: '#666', fontSize: '0.85em', marginTop: '2px' } },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.history.imported.note" })),
        React.createElement("ul", { style: { margin: '4px 0 0', paddingLeft: '16px' } }, entry.importedHeadings.map(function (h, i) { return (
        // eslint-disable-next-line react/no-array-index-key -- Aleph order, forms may repeat
        React.createElement("li", { key: i, "data-testid": "record-history-imported-heading" },
            React.createElement("span", { style: { fontFamily: 'monospace', marginRight: '8px' } }, h.fieldTag),
            h.heading)); }))));
};
/**
 * CZNA-586 (AR-10) — read-only version history of a record: for every version who and
 * when, and on demand what changed, subfield by subfield (`old` → `new`). A change of subfield
 * order is one row per field occurrence with the whole field before and after (CZNA-1391). NK comment 6:
 * „Počítáme s verzováním záznamu, kde bude patrné, jaké změny byly provedeny."
 *
 * Bounded twice so a long-lived or heavily edited record never renders an endless list:
 * versions come {@link VERSIONS_PER_PAGE} at a time ("Načíst starší verze"), and each
 * version is collapsed to its change count until opened, then shows the first
 * {@link CHANGES_SHOWN_COLLAPSED} changes behind "Zobrazit všech N".
 *
 * The history ends with the heading forms imported from Aleph, when the record has any —
 * one entry of kind `IMPORTED_HEADINGS` that the server always puts last.
 *
 * The history is fetched only once the accordion is opened — most visits to a record
 * never look at it. The field/old/new table has the shape of `ui-czna-users`'
 * `AuditLogDetailPane` changes table, deliberately: one way of reading a change.
 */
var RecordHistoryPanel = function (_a) {
    var id = _a.id;
    var ky = useOkapiKy();
    var _b = useState(false), open = _b[0], setOpen = _b[1];
    var _c = useInfiniteQuery(['record-history', id], function (_a) {
        var _b = _a.pageParam, pageParam = _b === void 0 ? 0 : _b;
        return getRecordHistory(ky, id, pageParam, VERSIONS_PER_PAGE);
    }, {
        enabled: open && !!id,
        getNextPageParam: function (lastPage, pages) { return (lastPage.hasNextPage ? pages.length * VERSIONS_PER_PAGE : undefined); },
    }), data = _c.data, isLoading = _c.isLoading, isError = _c.isError, hasNextPage = _c.hasNextPage, fetchNextPage = _c.fetchNextPage, isFetchingNextPage = _c.isFetchingNextPage;
    var versions = useMemo(function () { var _a; return ((_a = data === null || data === void 0 ? void 0 : data.pages) !== null && _a !== void 0 ? _a : []).flatMap(function (page) { var _a; return (_a = page.items) !== null && _a !== void 0 ? _a : []; }); }, [data]);
    var getUserName = useUserNames(versions.flatMap(function (v) { return (v.kind === 'IMPORTED_HEADINGS' ? [] : [v.changedBy]); })).getUserName;
    return (React.createElement(Accordion, { id: "record-history-accordion", label: React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.history.label" }), open: open, onToggle: function () { return setOpen(function (o) { return !o; }); } },
        isLoading && React.createElement(Loading, null),
        isError && (React.createElement(MessageBanner, { type: "error" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.history.loadError" }))),
        !isLoading && !isError && data && versions.length === 0 && (React.createElement("p", { "data-testid": "record-history-empty" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.history.empty" }))),
        versions.length > 0 && (React.createElement("ul", { "data-testid": "record-history-list", style: { padding: 0, margin: 0 } }, versions.map(function (v) {
            var _a;
            return (v.kind === 'IMPORTED_HEADINGS'
                ? React.createElement(ImportedHeadingsRow, { key: "imported-headings", entry: v })
                : (React.createElement(VersionRow, { key: v.changeId, entry: v, userName: (_a = getUserName(v.changedBy)) !== null && _a !== void 0 ? _a : v.changedBy })));
        }))),
        hasNextPage && (React.createElement(Button, { onClick: function () { return fetchNextPage(); }, disabled: isFetchingNextPage, "data-testid": "record-history-load-more" },
            React.createElement(FormattedMessage, { id: "ui-czna-authorities.authorities.detail.history.loadMore" })))));
};
export default RecordHistoryPanel;
